import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import toast from "../../components/sweetalert/Sweetalert";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import DeviceLoader from "../../components/loader/DeviceLoader";

const Devices = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const uname = sessionStorage.getItem('uname') || '';
    const department = sessionStorage.getItem('department') || '';
    const departmentslug = sessionStorage.getItem('departmentslug') || '';

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const userDeviceKey = ['userDevices', departmentslug, currentPage, rowPerPage];

    const tableColumns = [
        { name: 'Name', selector: row => row.name },
        { name: 'IP Address', selector: row => row.ipaddress },
        { name: 'Browser', selector: row => row.browser },
        { name: 'Device', selector: row => row.device },
        { name: 'OS', selector: row => row.os },
        { name: 'Location', selector: row => row.location },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const updateUserDeviceMutation = useMutation({
        mutationFn: async (userDeviceData) => {
            const res = await axios.patch('devices', userDeviceData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(userDeviceKey);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.response || 'Something went wrong while updating the status of user device!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleUpdateUserDevice = (id, status, label) => {
        Swal.fire({
            text: `Are you sure you want to ${label} this device?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(async function(resolve) {
                    if (result) {
                        updateUserDeviceMutation.mutate({
                            deviceid: id,
                            status,
                            uname,
                            department,
                            departmentcode: departmentslug
                        });
                        resolve();
                    }  
                })
            }
        });
    };

    const updateUserDeviceLocationMutation = useMutation({
        mutationFn: async (userDeviceData) => {
            const res = await axios.put('devices', userDeviceData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onMutate: (userDeviceData) => {
            const previousDevices = queryClient.getQueryData(userDeviceKey);

            queryClient.setQueryData(userDeviceKey, (oldData) => {
                return {
                    ...oldData,
                    data: oldData.data.map((device) =>
                        device.id === userDeviceData.deviceid
                            ? { ...device, location: userDeviceData.location }
                            : device
                    )
                };
            });

            return { previousDevices };
        },
        onError: (error, userDeviceData, context) => {
            // Revert to the previous state in case of an error
            queryClient.setQueryData(userDeviceKey, context.previousDevices);
            toast.fire({ icon: 'error', title: 'Error updating location' });
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(userDeviceKey);
        }
    });

    const handleUpdateUserDeviceLocation = (id, currentLocation) => {
        const newLocation = currentLocation === 1 ? 0 : 1;

        queryClient.setQueryData(userDeviceKey, (oldData) => {
            return {
                ...oldData,
                data: oldData.data.map((device) =>
                    device.id === id
                        ? { ...device, location: newLocation }
                        : device
                )
            };
        });

        updateUserDeviceLocationMutation.mutate({
            deviceid: id,
            location: newLocation,
            uname,
            department,
            departmentcode: departmentslug
        });
    };

    const { data: userdevices, isLoading, error: errorUserDevices } = useQuery({
        queryKey: userDeviceKey,
        queryFn: async () => {
            const res = await axios.get(`devices?department=${departmentslug}&page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.devices?.map(item => ({
                name: item.name,
                ipaddress: item.ipaddress,
                browser: item.browser,
                device: item.device,
                os: item.os,
                location: (
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={item.location === 1}
                            onChange={() => handleUpdateUserDeviceLocation(item.id, item.location)}
                        />
                        <span className="slider"></span>
                    </label>
                ),
                status: item.status === 1 ? (
                    <span className="label label-success">Active</span>
                ) : item.status === 2 ? (
                    <span className="label label-danger">Rejected</span>
                ) : (
                    <span className="label label-primary">Pending</span>
                ),
                action: (
                    item.status === 1 ? (
                        <div className="buttons">
                            <button
                                type="button"
                                className="btn-reject"
                                title="Reject"
                                onClick={() => handleUpdateUserDevice(item.id, 2, 'REJECT')}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    ) : item.status === 2 ? (
                        <div className="buttons">
                            <button
                                type="button"
                                className="btn-approve"
                                title="Approve"
                                onClick={() => handleUpdateUserDevice(item.id, 1, 'APPROVE')}
                            >
                                <i className="fa fa-check"></i>
                            </button>
                        </div>
                    ) : (
                        <div className="buttons">
                            <button
                                type="button"
                                className="btn-approve"
                                title="Approve"
                                onClick={() => handleUpdateUserDevice(item.id, 1, 'APPROVE')}
                            >
                                <i className="fa fa-check"></i>
                            </button>
                            <button
                                type="button"
                                className="btn-reject"
                                title="Reject"
                                onClick={() => handleUpdateUserDevice(item.id, 2, 'REJECT')}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    )
                )
            }));

            return { data, totalRows: res.data.response?.totalRows };
        },
        enabled: !!departmentslug,
        retry: 1
    });

    useEffect(() => {
        if (errorUserDevices) {
            if (errorUserDevices.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            } else {
                const errorMessage = errorUserDevices.response?.data.response
                    ? errorUserDevices.response?.data.response
                    : 'Something went wrong while fetching devices!';
                toast.fire({ icon: 'error', title: errorMessage });
            }
        }
    }, [errorUserDevices]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    return (
        <div className="devices">
            <div className="page-header">
                <div className="title">
                    <h1>Devices</h1>
                    <ul className="breadcrumbs">
                        <li>
                            <NavLink to="/settings/devices">Settings</NavLink>
                        </li>
                        <li>
                            <span>Devices</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className="panel">
                    <DataTable
                        columns={tableColumns}
                        data={userdevices?.data}
                        progressPending={isLoading}
                        progressComponent={<DeviceLoader />}
                        pagination
                        paginationServer
                        paginationTotalRows={userdevices?.totalRows || 0}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        dense
                        highlightOnHover
                        responsive />
                </div>
            </div>
        </div>
    );
};

export default Devices;
